<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		AdvancedCard,
		Button,
		DeviceButton,
		Input,
		LabelList,
		ProgressBar,
		Select,
		Slider,
		Text,
	} from '@Shelly/shelly-design-system';
	//Helper
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { valueOutOfRange } from 'src/helpers/utilities';
	import { getVComponentStatus } from 'src/helpers/virtual_helpers/virtual_components_status_helpers';
	import { bthomeObjectTitle } from 'src/helpers/virtual_helpers/virtual_bthome_helper';
	import { real_id } from 'src/helpers/interface_helpers';
	//Components
	import VirtualButton from './VirtualButton.svelte';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Stores
		import { getDeviceStatus, getDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	//Types
	import type {
		VirtualComponentConfig,
		VirtualComponentInterface,
		VirtualComponentInterfaceData,
		VirtualComponentTypes,
		VirtualComponentUI,
	} from 'src/types/VirtualComponents';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';

	$: statusStore = getDeviceStatus(deviceID);
	$: settingsStore = getDeviceSettings(deviceID);
	export let component: VirtualComponentInterface;
	export let device: Device;
	export let showHiddenComponent: boolean = false;
	export let groupID: number = NaN;

	const deviceID = real_id(device.id);

	const prepareToSave = (value: any) => {
		valueToSave = value;
		savedValue = value;
	};

	const elOnFocus = () => {
		valueToSave = valueData.value;
		onFocus = true;
	};

	const elOnBlur = () => {
		onFocus = false;
	};

	const valueToTitle = (value: any): string => {
		if (type == 'boolean' && configUI.titles) {
			return configUI.titles[value ? 1 : 0] || value;
		}
		return value === undefined ? null : value;
	};

	const validateField = (value: string): string => {
		if (type == 'number' && valueOutOfRange(Number(value), config.min, config.max)) {
			return $format('min_max_error_description', {
				values: {
					min: config.min,
					max: config.max,
				},
				default: 'Value must be between {min} and {max}.',
			});
		} else if (type == 'text' && value.length >= config.max_len) {
			return $format('max_text_length_error_description', {
				values: { max_len: config.max_len },
				default: 'Value must be less then or equal to {max_len}.',
			});
		}
		return '';
	};

	const valueToPercent = (value: number, min: number, max: number) => {
		return ((value - min) * 100) / (max - min);
	};

	const saveValue = async () => {
		waitForResponse = true;
		const res: SettingRequest = await gen2Request(device, `${type}.set`, {
			id: config.id,
			value: valueToSave,
		});
		if (res?.isok) {
			valueData.value = valueToSave;
		} else {
			savedValue = null;
			if (sliderValue != null) {
				sliderValue = null;
			}
			onSettingsResponse(res, device);
		}
		waitForResponse = false;
	};

	const onChangeStatus = (_status, _config) => {
		if (waitForResponse || !_config) return;
		const _valueData: VirtualComponentInterfaceData = getVComponentStatus(
			device,
			[component.key],
			groupID,
			showHiddenComponent,
		)[0];
		if (_valueData) {
			type = component.key.split(':')[0] as VirtualComponentTypes;
			config = _config;
			configUI = _config?.['ui'] || _config?.meta?.ui || {};
			_valueData.value =
				(_valueData.event !== null ? _valueData.event : _valueData.value) ??
				_config.default_value;
			valueData.icon = _valueData.icon;
			valueData.iconColor = _valueData.iconColor;
			valueData.label = _valueData.label;
			valueData.unit = _valueData.unit;
			if (savedValue !== null) {
				if (savedValue == _valueData.value) savedValue = null;
			} else if (valueData.value != _valueData.value || _valueData.isBleButton) {
				valueData = _valueData;
			}
			if (savedValue == null) {
				sliderValue = _valueData.value;
			}
		}
	};

	let waitForResponse = false;
	let valueToSave = null;
	let savedValue = null;
	let onFocus = false;
	let type: VirtualComponentTypes;
	let config: VirtualComponentConfig = {};
	let configUI: VirtualComponentUI = {};
	let valueData: VirtualComponentInterfaceData = {
		event: null,
		value: null,
		unit: '',
		icon: '',
		iconColor: '',
		label: '',
	};
	let sliderValue = null;

	$: onChangeStatus($statusStore, $settingsStore?.[component.key]);
</script>

{#if config && (/^bthome/.test(component.key) || configUI.view != '' || showHiddenComponent)}
	<AdvancedCard
		size="m"
		class="mb-s ml-xs mr-xs"
		title={config.name || bthomeObjectTitle(config) || component.key}
		subtitle={showHiddenComponent
			? {
					value: config?.addr || component.key,
					bold: false,
					textSmall: true,
					type: 'secondary',
				}
			: null}
		optional={!/^bthome/.test(component.key) && (configUI.view === '' || configUI.view === 'hidden')
			? {
					value: $format('label_hidden', { default: 'hidden' }),
					bold: false,
					textSmall: true,
					type: 'muted',
				}
			: null}
		avatar={{
			image:
				configUI.icon ||
				(type == 'enum' && valueData.value != null && configUI.images?.[valueData.value]),
			icon: 'fas fa-cube',
		}}
		isFocusable={false}
		tabindex="-1"
	>
		<div
			slot="main"
			class="d-flex flex-direction-column width-full"
			style={configUI.view == 'progressbar' ? 'pointer-events:none;' : ''}
		>
			{#if type == 'enum' && valueData.label}
				<LabelList
					size="m"
					class="mt-xs"
					isDefaultCursor
					data-prevent-drawer-swipe
					data-prevent-sidepanel-swipe
					labelList={[
						{
							icon: valueData?.icon,
							iconColor: valueData?.iconColor,
							value: valueData.label,
						},
					]}
				/>
			{/if}
			{#if !/slider|progressbar|dropdown|image|button/.test(configUI.view) && !valueData.isBleButton && valueData.value !== null && !(configUI.view == 'label' && /^(enum|button)$/.test(type))}
				<LabelList
					size="m"
					class="mt-xs"
					isDefaultCursor
					data-prevent-drawer-swipe
					data-prevent-sidepanel-swipe
					labelList={[
						{
							icon: valueData.icon,
							iconColor: valueData.iconColor,
							value:
								valueData.label ||
								(valueData.value === undefined || valueData.value === ''
									? null
									: `${valueToTitle(valueData.value)} ${valueData.unit || ''}`),
						},
					]}
				/>
			{/if}
			{#if configUI.view == 'field'}
				<div class="d-flex">
					<Input
						aria-label={$format('input_value', { default: 'Input value' })}
						icon="fas fa-pen"
						class="mt-s"
						value={onFocus || waitForResponse || savedValue !== null
							? valueToSave
							: valueData.value}
						on:focus={elOnFocus}
						on:blur={elOnBlur}
						on:input={(e) => prepareToSave(e.target['value'])}
						error={onFocus && validateField(valueToSave)}
						id="input-hj3owfb"
					/>
					<Button size="m" class="mt-s ml-s" loading={waitForResponse} on:click={saveValue}>
						{$format('button_save')}
					</Button>
				</div>
			{:else if /slider/.test(configUI.view)}
				{@const a11yProps = {
					ariaLabel: $format('label_value', { default: 'Value' }),
					ariaLabelValue:
						valueData?.value != null ? `${valueData.value} ${valueData?.unit || ''}` : null,
					'aria-live': 'polite',
				}}
				<Slider
					id="slider-ly2evq6"
					{a11yProps}
					class="mt-xs"
					value={sliderValue}
					min={config.min}
					max={config.max}
					on:value_change={(e) => {
						prepareToSave(e.detail.value);
						saveValue();
					}}
					unit={' ' + (configUI.unit || '')}
					step={Number(configUI?.step || 1)}
					title=" "
					type="simple"
				/>
			{:else if /progressbar/.test(configUI.view)}
				<ProgressBar
					class="mt-s width-full"
					type="primary"
					progress={valueToPercent(valueData.value, config.min, config.max)}
				/>
				<Text styleType="body-small" class="mt-s mb-s text-center text-bold"
					>{`${valueData.value} ${configUI.unit || ''}`}</Text
				>
			{:else if configUI.view == 'dropdown'}
				<Select
					aria-label={$format('select_value', { default: 'Select value' })}
					class="width-full"
					value={valueData.value != null ? valueData.value : config.options[0]}
					options={config.options}
					icon={null}
					on:change={(e) => {
						prepareToSave(e.detail);
						saveValue();
					}}
					id="select-ym2rkbj"
				/>
			{:else if configUI.view == 'image'}
				<img
					src={valueData.value}
					width="100%"
					alt={`img URL: ${valueData.value}`}
					style="object-fit:contain;max-height: 150px;"
				/>
			{/if}
		</div>
		<svelte:fragment slot="right">
			{#if /^(button|boolean)$/.test(type) || valueData.isBleButton}
				{#if type == 'button' || valueData.isBleButton}
					<VirtualButton
						{device}
						componentID={config.id}
						value={valueData.value}
						icon={valueData.icon}
						disabled={valueData.isBleButton}
					/>
				{:else}
					{@const interactiveBtn = /^(toggle|button)$/.test(configUI.view)}
					<div
						role="presentation"
						class="d-flex align-items-center{waitForResponse || interactiveBtn
							? ' cursor-pointer'
							: ''}"
						on:click={() => {
							if (!interactiveBtn) return;
							prepareToSave(!valueData.value);
							saveValue();
						}}
					>
						{#if configUI.buttonIcons?.[0] && configUI.buttonIcons?.[1]}
							<img
								src={configUI.buttonIcons[valueData.value ? 1 : 0]}
								width="100%"
								alt="img"
								style="object-fit:contain;max-width: 60px;max-height: 60px;"
							/>
						{:else if interactiveBtn}
							<DeviceButton
								id="devicebutton-7voy5df"
								aria-label={valueData.value
									? $format('label_turn_off', { default: 'Turn off' })
									: $format('label_turn_on', { default: 'Turn on' })}
								icon="fas fa-power-off"
								isLoading={waitForResponse}
								isActive={valueData.value}
							/>
						{/if}
					</div>
				{/if}
			{/if}
			<slot />
		</svelte:fragment>
	</AdvancedCard>
{/if}
